import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { ExternalLink, Gift, CornerUpLeft, Truck, CreditCard, Package } from "lucide-react";
const WIDGET_KEYFRAMES = `
@keyframes voice-order-detail-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
const STATUS_PILL = {
  placed: { label: "Placed", bg: "#F1EFE8", fg: "#444441", dot: "#888780" },
  processing: { label: "Processing", bg: "#FAEEDA", fg: "#633806", dot: "#BA7517" },
  shipped: { label: "Shipped", bg: "#E6F1FB", fg: "#0C447C", dot: "#185FA5" },
  out_for_delivery: { label: "Out for delivery", bg: "#E6F1FB", fg: "#0C447C", dot: "#185FA5" },
  delivered: { label: "Delivered", bg: "#E8F3E4", fg: "#1F4711", dot: "#3B6D11" },
  cancelled: { label: "Cancelled", bg: "#FCEBEB", fg: "#791F1F", dot: "#A32D2D" },
  returned: { label: "Returned", bg: "#F1EFE8", fg: "#444441", dot: "#888780" },
  unknown: { label: "Unknown", bg: "#F1EFE8", fg: "#444441", dot: "#888780" }
};
const RETURN_PILL_BG = "#FAEEDA";
const RETURN_PILL_FG = "#633806";
const RETURN_PILL_FG_MUTED = "#854F0B";
const REWARD_CHIP_BG = "#EEEDFE";
const REWARD_CHIP_FG = "#26215C";
const SHIPPING_FREE_FG = "#3B6D11";
const SURFACE = "var(--voice-widget-card-bg, #FFFFFF)";
const BORDER = "var(--voice-widget-border, #E5E7EB)";
const FG = "var(--voice-widget-fg, #111827)";
const MUTED = "var(--voice-widget-muted, #6B7280)";
const IMG_BG = "var(--voice-widget-img-bg, #F9FAFB)";
const ACCENT_DARK = "var(--voice-widget-accent-dark, #0D9488)";
const CURRENCY_SYMBOL = { USD: "$", CAD: "$", EUR: "€", GBP: "£" };
function money(amount, currency = "USD") {
  if (amount === void 0 || amount === null || Number.isNaN(amount)) return void 0;
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  return `${symbol}${Number(amount).toFixed(2)}`;
}
function formatPlacedAt(iso) {
  if (!iso) return void 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return void 0;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function normalizeIssuerLabel(issuer) {
  if (!issuer) return void 0;
  const v = issuer.trim().toLowerCase();
  if (v === "visa") return "VISA";
  if (v === "mastercard" || v === "master" || v === "master-card") return "MASTERCARD";
  if (v === "american-express" || v === "amex" || v === "american_express") return "AMEX";
  if (v === "discover") return "DISCOVER";
  return issuer.toUpperCase();
}
function ItemImage({ src, alt }) {
  const [errored, setErrored] = useState(false);
  const showPlaceholder = !src || errored;
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        width: 88,
        height: 88,
        flexShrink: 0,
        background: IMG_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden"
      },
      children: showPlaceholder ? /* @__PURE__ */ jsx(Package, { size: 28, strokeWidth: 1.5, color: MUTED, "aria-hidden": true }) : /* @__PURE__ */ jsx(
        "img",
        {
          src,
          alt,
          onError: () => setErrored(true),
          style: { width: "100%", height: "100%", objectFit: "contain", display: "block" }
        }
      )
    }
  );
}
function SectionLabel({ icon, text }) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: MUTED,
        marginBottom: 10
      },
      children: [
        icon,
        text
      ]
    }
  );
}
function SummaryRow({
  label,
  value,
  valueColor,
  valueWeight
}) {
  if (value === void 0) return null;
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "baseline", justifyContent: "space-between" }, children: [
    /* @__PURE__ */ jsx("span", { style: { color: MUTED }, children: label }),
    /* @__PURE__ */ jsx(
      "span",
      {
        style: {
          fontVariantNumeric: "tabular-nums",
          color: valueColor ?? FG,
          fontWeight: valueWeight ?? 400
        },
        children: value
      }
    )
  ] });
}
function ItemRow({
  item,
  currency,
  isLast
}) {
  const itemCurrency = item.currency ?? currency;
  const showStrike = item.currentRetailPrice !== void 0 && item.currentRetailPrice !== item.pricePaid;
  const subtitleBits = [item.subtitle, item.qty ? `Qty ${item.qty}` : void 0].filter(Boolean);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        gap: 16,
        padding: "20px 24px",
        borderBottom: isLast ? void 0 : `1px solid ${BORDER}`
      },
      children: [
        /* @__PURE__ */ jsx(ItemImage, { src: item.imageUrl, alt: item.title }),
        /* @__PURE__ */ jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [
          item.brand && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 4
              },
              children: item.brand
            }
          ),
          /* @__PURE__ */ jsx("div", { style: { fontSize: 15, fontWeight: 500, color: FG, lineHeight: 1.35 }, children: item.title }),
          (subtitleBits.length > 0 || item.sellerName) && /* @__PURE__ */ jsxs("div", { style: { fontSize: 12.5, color: MUTED, marginTop: 4 }, children: [
            subtitleBits.join(" · "),
            item.sellerName && /* @__PURE__ */ jsxs(Fragment, { children: [
              subtitleBits.length > 0 && /* @__PURE__ */ jsx("span", { style: { margin: "0 6px", color: BORDER }, children: "·" }),
              /* @__PURE__ */ jsxs("span", { children: [
                "Sold by ",
                item.sellerName
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: "4px 10px",
                marginTop: 10
              },
              children: [
                /* @__PURE__ */ jsx("span", { style: { fontSize: 17, fontWeight: 600, color: FG, fontVariantNumeric: "tabular-nums" }, children: money(item.pricePaid, itemCurrency) }),
                showStrike && /* @__PURE__ */ jsx(
                  "span",
                  {
                    style: {
                      fontSize: 12.5,
                      color: MUTED,
                      textDecoration: "line-through",
                      fontVariantNumeric: "tabular-nums"
                    },
                    children: money(item.currentRetailPrice, itemCurrency)
                  }
                ),
                item.rewardEarned !== void 0 && item.rewardEarned > 0 && /* @__PURE__ */ jsxs(
                  "span",
                  {
                    style: {
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 11,
                      fontWeight: 500,
                      color: REWARD_CHIP_FG,
                      background: REWARD_CHIP_BG,
                      padding: "2px 8px",
                      borderRadius: 999
                    },
                    children: [
                      /* @__PURE__ */ jsx(Gift, { size: 12, strokeWidth: 2, "aria-hidden": true }),
                      money(item.rewardEarned, itemCurrency),
                      " reward earned"
                    ]
                  }
                )
              ]
            }
          ),
          item.shipmentStatus && /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginTop: 8 }, children: item.shipmentStatusMessage ?? item.shipmentStatus }),
          item.returnStatus && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: 12,
                fontSize: 12,
                fontWeight: 500,
                color: RETURN_PILL_FG,
                background: RETURN_PILL_BG,
                padding: "4px 10px",
                borderRadius: 6
              },
              children: [
                /* @__PURE__ */ jsx(CornerUpLeft, { size: 13, strokeWidth: 2, "aria-hidden": true }),
                item.returnStatus.label,
                item.returnStatus.reason && /* @__PURE__ */ jsxs("span", { style: { fontWeight: 400, color: RETURN_PILL_FG_MUTED }, children: [
                  "· ",
                  item.returnStatus.reason
                ] })
              ]
            }
          )
        ] })
      ]
    }
  );
}
function ShippingSection({ shipping }) {
  const cityLine = [shipping.city, shipping.state].filter(Boolean).join(", ");
  return /* @__PURE__ */ jsxs("div", { style: { background: SURFACE, padding: "20px 24px" }, children: [
    /* @__PURE__ */ jsx(
      SectionLabel,
      {
        icon: /* @__PURE__ */ jsx(Truck, { size: 13, strokeWidth: 2, color: MUTED, "aria-hidden": true }),
        text: shipping.isPickup ? "Pickup" : "Shipping"
      }
    ),
    /* @__PURE__ */ jsxs("div", { style: { fontSize: 13, lineHeight: 1.55, color: FG }, children: [
      shipping.recipientName && /* @__PURE__ */ jsx("div", { children: shipping.recipientName }),
      shipping.addressLine1 && /* @__PURE__ */ jsx("div", { children: shipping.addressLine1 }),
      shipping.addressLine2 && /* @__PURE__ */ jsx("div", { children: shipping.addressLine2 }),
      (cityLine || shipping.zipCode) && /* @__PURE__ */ jsxs("div", { children: [
        cityLine,
        shipping.zipCode ? ` ${shipping.zipCode}` : ""
      ] }),
      shipping.country && /* @__PURE__ */ jsx("div", { children: shipping.country })
    ] }),
    (shipping.method || shipping.etaLabel) && /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginTop: 10 }, children: [shipping.method, shipping.etaLabel].filter(Boolean).join(" · ") }),
    shipping.trackingUrl && /* @__PURE__ */ jsxs(
      "a",
      {
        href: shipping.trackingUrl,
        target: "_blank",
        rel: "noopener noreferrer",
        style: {
          marginTop: 8,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 12,
          fontWeight: 500,
          color: ACCENT_DARK,
          textDecoration: "none"
        },
        children: [
          "Track",
          shipping.carrier ? ` with ${shipping.carrier}` : "",
          /* @__PURE__ */ jsx(ExternalLink, { size: 12, strokeWidth: 2, "aria-hidden": true })
        ]
      }
    )
  ] });
}
function PaymentSection({ payment, currency }) {
  const issuerLabel = normalizeIssuerLabel(payment.issuer);
  return /* @__PURE__ */ jsxs("div", { style: { background: SURFACE, padding: "20px 24px" }, children: [
    /* @__PURE__ */ jsx(
      SectionLabel,
      {
        icon: /* @__PURE__ */ jsx(CreditCard, { size: 13, strokeWidth: 2, color: MUTED, "aria-hidden": true }),
        text: "Payment"
      }
    ),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: FG }, children: [
      issuerLabel && /* @__PURE__ */ jsx(
        "span",
        {
          style: {
            background: FG,
            color: SURFACE,
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.05em"
          },
          children: issuerLabel
        }
      ),
      payment.last4 && /* @__PURE__ */ jsxs("span", { children: [
        "ending in ",
        payment.last4
      ] }),
      !issuerLabel && !payment.last4 && payment.methodLabel && /* @__PURE__ */ jsx("span", { children: payment.methodLabel })
    ] }),
    payment.cardHolderName && /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginTop: 4 }, children: payment.cardHolderName }),
    payment.billedToSame && /* @__PURE__ */ jsx("div", { style: { fontSize: 12, color: MUTED, marginTop: 10 }, children: "Billed to same address" }),
    payment.giftCardApplied !== void 0 && payment.giftCardApplied > 0 && /* @__PURE__ */ jsxs("div", { style: { fontSize: 12, color: MUTED, marginTop: 4 }, children: [
      "Gift card applied: ",
      money(payment.giftCardApplied, currency)
    ] })
  ] });
}
function SummarySection({ summary, currency }) {
  return /* @__PURE__ */ jsxs("div", { style: { background: IMG_BG, padding: "20px 24px", borderTop: `1px solid ${BORDER}` }, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: MUTED,
          marginBottom: 12
        },
        children: "Order summary"
      }
    ),
    /* @__PURE__ */ jsxs("div", { style: { fontSize: 13, display: "flex", flexDirection: "column", gap: 6 }, children: [
      /* @__PURE__ */ jsx(SummaryRow, { label: "Subtotal", value: money(summary.subtotal, currency) }),
      summary.shipping !== void 0 && /* @__PURE__ */ jsx(
        SummaryRow,
        {
          label: "Shipping",
          value: summary.shipping === 0 ? "Free" : money(summary.shipping, currency),
          valueColor: summary.shipping === 0 ? SHIPPING_FREE_FG : void 0,
          valueWeight: summary.shipping === 0 ? 500 : void 0
        }
      ),
      summary.shippingDiscount !== void 0 && summary.shippingDiscount > 0 && /* @__PURE__ */ jsx(SummaryRow, { label: "Shipping discount", value: `−${money(summary.shippingDiscount, currency)}` }),
      summary.couponDiscount !== void 0 && summary.couponDiscount > 0 && /* @__PURE__ */ jsx(SummaryRow, { label: "Coupon", value: `−${money(summary.couponDiscount, currency)}` }),
      summary.giftCard !== void 0 && summary.giftCard > 0 && /* @__PURE__ */ jsx(SummaryRow, { label: "Gift card", value: `−${money(summary.giftCard, currency)}` }),
      summary.tax !== void 0 && /* @__PURE__ */ jsx(SummaryRow, { label: "Tax", value: money(summary.tax, currency) }),
      summary.serviceFee !== void 0 && summary.serviceFee > 0 && /* @__PURE__ */ jsx(SummaryRow, { label: "Service fee", value: money(summary.serviceFee, currency) })
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${BORDER}`
        },
        children: [
          /* @__PURE__ */ jsx("span", { style: { fontSize: 13, fontWeight: 500, color: FG }, children: "Total" }),
          /* @__PURE__ */ jsx("span", { style: { fontSize: 19, fontWeight: 600, color: FG, fontVariantNumeric: "tabular-nums" }, children: money(summary.total, currency) })
        ]
      }
    )
  ] });
}
const OrderDetailWidget = ({ data }) => {
  var _a;
  const currency = ((_a = data.summary) == null ? void 0 : _a.currency) ?? "USD";
  const pill = STATUS_PILL[data.status] ?? STATUS_PILL.unknown;
  const placedAt = formatPlacedAt(data.placedAt);
  const hasShipping = !!data.shipping;
  const hasPayment = !!data.payment;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        width: "100%",
        maxWidth: 640,
        maxHeight: "100%",
        boxSizing: "border-box",
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        animation: "voice-order-detail-fade-in 0.28s ease-out both",
        display: "flex",
        flexDirection: "column"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              padding: "20px 24px",
              borderBottom: `1px solid ${BORDER}`
            },
            children: [
              /* @__PURE__ */ jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: 10
                    },
                    children: [
                      /* @__PURE__ */ jsxs(
                        "span",
                        {
                          style: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "3px 10px",
                            borderRadius: 999,
                            fontSize: 11.5,
                            fontWeight: 500,
                            letterSpacing: "0.02em",
                            background: pill.bg,
                            color: pill.fg
                          },
                          children: [
                            /* @__PURE__ */ jsx(
                              "span",
                              {
                                style: { width: 6, height: 6, borderRadius: "50%", background: pill.dot, display: "inline-block" },
                                "aria-hidden": true
                              }
                            ),
                            pill.label
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsxs("span", { style: { fontSize: 11.5, color: MUTED, fontVariantNumeric: "tabular-nums" }, children: [
                        "#",
                        data.orderId
                      ] })
                    ]
                  }
                ),
                data.headline && /* @__PURE__ */ jsx("div", { style: { fontSize: 21, fontWeight: 600, color: FG, lineHeight: 1.2, letterSpacing: "-0.01em" }, children: data.headline }),
                data.subHeadline && /* @__PURE__ */ jsx("div", { style: { fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.4 }, children: data.subHeadline }),
                (placedAt || data.retailerName) && /* @__PURE__ */ jsxs("div", { style: { fontSize: 13, color: MUTED, marginTop: 8 }, children: [
                  placedAt && /* @__PURE__ */ jsxs("span", { children: [
                    "Placed ",
                    placedAt
                  ] }),
                  placedAt && data.retailerName && /* @__PURE__ */ jsx("span", { style: { margin: "0 6px", color: BORDER }, children: "·" }),
                  data.retailerName && /* @__PURE__ */ jsx("span", { children: data.retailerName })
                ] })
              ] }),
              data.retailerOrderPageUrl && /* @__PURE__ */ jsxs(
                "a",
                {
                  href: data.retailerOrderPageUrl,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  style: {
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${BORDER}`,
                    background: SURFACE,
                    color: FG,
                    fontSize: 12,
                    fontWeight: 500,
                    textDecoration: "none",
                    whiteSpace: "nowrap"
                  },
                  children: [
                    "View on ",
                    data.retailerName ?? "retailer",
                    /* @__PURE__ */ jsx(ExternalLink, { size: 12, strokeWidth: 2, "aria-hidden": true })
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              maxHeight: 320,
              overflowY: "auto"
            },
            children: data.items.map((item, i) => /* @__PURE__ */ jsx(ItemRow, { item, currency, isLast: i === data.items.length - 1 }, i))
          }
        ),
        (hasShipping || hasPayment) && /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: hasShipping && hasPayment ? "1fr 1fr" : "1fr",
              gap: 1,
              background: BORDER,
              borderTop: `1px solid ${BORDER}`
            },
            children: [
              hasShipping && data.shipping && /* @__PURE__ */ jsx(ShippingSection, { shipping: data.shipping }),
              hasPayment && data.payment && /* @__PURE__ */ jsx(PaymentSection, { payment: data.payment, currency })
            ]
          }
        ),
        /* @__PURE__ */ jsx(SummarySection, { summary: data.summary, currency })
      ]
    }
  );
};
export {
  OrderDetailWidget
};
//# sourceMappingURL=OrderDetailWidget.js.map
